/**
 * 拓客工作台(1025):获客舱里嵌入拓客平台的页面。
 *
 * - 账号:后端按登录人手机号换免登链接(1029 起经销帮 sso,两边开户手机号一致即打通);积分 / 套餐归平台管,我们这边不扣。
 * - 数据:1029 用户定解锁的企业**不进我方线索库**,员工就在平台页面里看、用;对客文案不提「自动进线索库」。
 *   (回推口与认人代码保留未拆,平台没配推送地址就不会有数据进来。)
 * - 登录凭据一次性:切页签 / 新窗口打开都现换一条,不复用。
 */
import { useCallback, useEffect, useState } from "react";
import { ExternalLink, RefreshCw } from "lucide-react";

import { errorText } from "@/shared/lib/apiErrors";
import { cn } from "@/shared/lib/utils";

import { QIKE_MODULE_LABELS, qikeService, type QikeStatus } from "../api/qikeService";
import { AcqShell } from "./AcqShell";

/** 窄屏(企微 / 手机)用启客的移动端页面。 */
function isNarrow(): boolean {
  return typeof window !== "undefined" && window.innerWidth < 768;
}

export default function QikeWorkbenchPage() {
  const [status, setStatus] = useState<QikeStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [module, setModule] = useState<string>("searchHome");
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    qikeService
      .status()
      .then((s) => alive && setStatus(s))
      .catch(
        (e: unknown) =>
          alive && setStatusError(errorText(e, { fallbackTitle: "拓客工作台加载失败" }))
      );
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!status?.enabled) return;
    let alive = true;
    setLoading(true);
    setError(null);
    setUrl(null);
    qikeService
      .link(module, isNarrow())
      .then((r) => alive && setUrl(r.url))
      .catch(
        (e: unknown) => alive && setError(errorText(e, { fallbackTitle: "进入失败,请稍后重试" }))
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [status?.enabled, module, nonce]);

  /** 新窗口:另换一条(iframe 那条可能已被用掉)。 */
  const openNewWindow = useCallback(async () => {
    const w = window.open("", "_blank");
    try {
      const r = await qikeService.link(module, false);
      if (w) w.location.href = r.url;
    } catch (e: unknown) {
      w?.close();
      setError(errorText(e, { fallbackTitle: "进入失败,请稍后重试" }));
    }
  }, [module]);

  const modules = (status?.modules ?? []).filter((m) => QIKE_MODULE_LABELS[m]);

  return (
    <AcqShell subtitle="在拓客平台里按关键词、行业、地区找企业,解锁后直接在这里查看联系方式">
      {statusError ? (
        <Notice text={statusError} />
      ) : !status ? (
        <Notice text="正在加载…" />
      ) : !status.enabled ? (
        <Notice text="拓客工作台还没开通,请联系管理员。" />
      ) : (
        <div className="flex h-[calc(100vh-14rem)] min-h-[520px] flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <nav className="flex min-w-0 flex-wrap gap-1" aria-label="拓客工作台页面">
              {modules.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setModule(m)}
                  aria-pressed={m === module}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    m === module
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  {QIKE_MODULE_LABELS[m]}
                </button>
              ))}
            </nav>
            <div className="ml-auto flex items-center gap-3 text-sm">
              <button
                type="button"
                onClick={() => setNonce((n) => n + 1)}
                className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-primary"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                重新进入
              </button>
              <button
                type="button"
                onClick={() => void openNewWindow()}
                className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-primary"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                新窗口打开
              </button>
            </div>
          </div>
          {error ? (
            <Notice text={error} />
          ) : loading || !url ? (
            <Notice text="正在进入…" />
          ) : (
            <iframe
              key={url}
              src={url}
              title="拓客工作台"
              className="w-full flex-1 rounded-lg border bg-background"
            />
          )}
        </div>
      )}
    </AcqShell>
  );
}

function Notice({ text }: { text: string }) {
  return (
    <div className="grid min-h-[320px] flex-1 place-items-center rounded-lg border bg-background">
      <p className="max-w-md px-6 text-center text-sm text-foreground">{text}</p>
    </div>
  );
}
